import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Filter, ChevronDown, X, ArrowRight } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CarCard from '../components/ui/CarCard';
import SeoHead from '../components/seo/SeoHead';
import { publicService } from '../services/public.service';
import { renterService } from '../services/renter.service';
import { showAppAlert } from '../utils/appAlert';

const BrowseFleet = () => {
  const location = useLocation();
  const initialParams = new URLSearchParams(location.search);

  const [searchQuery, setSearchQuery] = useState(initialParams.get('search') || '');
  const [selectedCity, setSelectedCity] = useState(initialParams.get('city') || '');
  const [activeCategory, setActiveCategory] = useState(initialParams.get('category') || 'All');
  const [selectedTransmission, setSelectedTransmission] = useState(initialParams.get('transmission') || '');
  const [selectedFuelType, setSelectedFuelType] = useState(initialParams.get('fuelType') || '');
  const [minSeats, setMinSeats] = useState(initialParams.get('minSeats') || '');
  const [maxSeats, setMaxSeats] = useState(initialParams.get('maxSeats') || '');
  const [sortBy, setSortBy] = useState(initialParams.get('sortBy') || 'Featured');
  const [minPrice, setMinPrice] = useState(initialParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(initialParams.get('maxPrice') || '');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [cars, setCars] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]);

  const [categories, setCategories] = useState(['All']);
  const [cities, setCities] = useState([]);
  const isRenter = !!localStorage.getItem('token');

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [cats, cityOptions] = await Promise.all([
          publicService.getCategories(),
          publicService.getCities()
        ]);
        setCategories(['All', ...cats]);
        setCities(cityOptions || []);
      } catch (err) {
        console.error('Failed to fetch browse filters', err);
      }
    };

    fetchFilterData();
  }, []);

  useEffect(() => {
    const loadWishlist = async () => {
      if (!isRenter) {
        setWishlistIds([]);
        return;
      }

      try {
        const ids = await renterService.getWishlistIds();
        setWishlistIds(ids || []);
      } catch (error) {
        setWishlistIds([]);
      }
    };

    loadWishlist();
  }, [isRenter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get('search') || '');
    setSelectedCity(params.get('city') || '');
    setActiveCategory(params.get('category') || 'All');
    setSelectedTransmission(params.get('transmission') || '');
    setSelectedFuelType(params.get('fuelType') || '');
    setMinSeats(params.get('minSeats') || '');
    setMaxSeats(params.get('maxSeats') || '');
    setSortBy(params.get('sortBy') || 'Featured');
    setMinPrice(params.get('minPrice') || '');
    setMaxPrice(params.get('maxPrice') || '');
    setPage(0);
  }, [location.search]);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        setLoading(true);
        const params = {
          page,
          size: 9,
          search: searchQuery || undefined,
          city: selectedCity || undefined,
          category: activeCategory !== 'All' ? activeCategory : undefined,
          transmission: selectedTransmission || undefined,
          fuelType: selectedFuelType || undefined,
          minSeats: minSeats ? Number(minSeats) : undefined,
          maxSeats: maxSeats ? Number(maxSeats) : undefined,
          minPrice: minPrice ? Number(minPrice) : undefined,
          maxPrice: maxPrice ? Number(maxPrice) : undefined,
          sortBy: sortBy !== 'Featured' ? sortBy : undefined,
        };

        const res = await publicService.getAvailableCars(params);
        const mappedCars = res.content.map((car) => ({
          id: car.id,
          name: `${car.make || 'Custom'} ${car.model || 'Model'}`,
          price: car.pricePerDay || 0,
          image: car.primaryImageUrl,
          category: car.categoryName || 'General',
          cityName: car.cityName,
          transmission: car.transmission,
          fuelType: car.fuelType,
          seats: car.seats,
          averageRating: car.averageRating,
          reviewCount: car.reviewCount,
        }));

        setCars(mappedCars);
        setTotalPages(res.totalPages);
        setTotalElements(res.totalElements);
      } catch (err) {
        console.error('Failed to fetch available cars', err);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(fetchCars, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedCity, activeCategory, selectedTransmission, selectedFuelType, minSeats, maxSeats, sortBy, minPrice, maxPrice, page]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCity, activeCategory, selectedTransmission, selectedFuelType, minSeats, maxSeats, sortBy, minPrice, maxPrice]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
    setActiveCategory('All');
    setSelectedTransmission('');
    setSelectedFuelType('');
    setMinSeats('');
    setMaxSeats('');
    setSortBy('Featured');
    setMinPrice('');
    setMaxPrice('');
  };

  const handleToggleWishlist = async (car) => {
    const isSaved = wishlistIds.includes(car.id);
    if (isSaved) {
      await renterService.removeFromWishlist(car.id);
      setWishlistIds((current) => current.filter((id) => id !== car.id));
      showAppAlert({
        type: 'success',
        title: 'Removed from wishlist',
        message: 'The vehicle has been removed from your saved cars.'
      });
      return;
    }

    await renterService.addToWishlist(car.id);
    setWishlistIds((current) => [...current, car.id]);
    showAppAlert({
      type: 'success',
      title: 'Added to wishlist',
      message: 'The vehicle has been added to your saved cars.'
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <SeoHead
        title="Browse Cars | Go Rides"
        description="Explore available cars by city, category, transmission, fuel type, seats, and price. Compare premium rentals and reserve with Go Rides."
        canonicalPath={`/browse${location.search || ''}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Browse Cars',
          url: `${window.location.origin}/browse${location.search || ''}`,
          description: 'Available rental cars on Go Rides'
        }}
      />
      <Navbar />

      <div className="max-w-7xl mx-auto w-full px-4 pt-12">
        <section className="rounded-3xl bg-brand-black text-white p-7 sm:p-10 flex flex-wrap justify-between items-center gap-6">
          <div className="max-w-2xl"><p className="text-brand-orange-500 text-sm font-semibold uppercase tracking-widest mb-3">Your journey starts here</p><h1 className="text-3xl sm:text-4xl font-bold">Find your next <span className="text-brand-orange-500">car.</span></h1><p className="mt-4 text-gray-300 leading-relaxed">Explore our fleet, compare your options, and choose the right car for your journey.</p></div>
          <Link to="/bookings" className="inline-flex items-center gap-2 rounded-xl border border-gray-600 px-5 py-3 text-sm font-semibold hover:bg-white hover:text-brand-black">My bookings <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
        </section>
      </div>

      <main className="flex-grow max-w-7xl mx-auto w-full px-4 pt-7 pb-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-64 space-y-8">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">Search</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search models..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">Category</h3>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeCategory === cat ? 'bg-brand-black text-white font-semibold' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">City</h3>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
              >
                <option value="">All Cities</option>
                {cities.map((city) => (
                  <option key={city.id || city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">Transmission</h3>
              <select
                value={selectedTransmission}
                onChange={(e) => setSelectedTransmission(e.target.value)}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
              >
                <option value="">All Types</option>
                <option value="Automatic">Automatic</option>
                <option value="Manual">Manual</option>
              </select>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">Fuel Type</h3>
              <select
                value={selectedFuelType}
                onChange={(e) => setSelectedFuelType(e.target.value)}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
              >
                <option value="">All Fuel Types</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Electric">Electric</option>
              </select>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">Seats</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  min="1"
                  placeholder="Min"
                  value={minSeats}
                  onChange={(e) => setMinSeats(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
                />
                <input
                  type="number"
                  min="1"
                  placeholder="Max"
                  value={maxSeats}
                  onChange={(e) => setMaxSeats(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4">Price Range</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  min="0"
                  placeholder="Min RF"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Max RF"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>
          </aside>

          <div className="lg:hidden flex items-center justify-between mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="flex items-center gap-2 text-sm font-bold text-brand-black"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{totalElements} vehicles</span>
            </div>
          </div>

          <div className="flex-1">
            <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-100 pb-6">
              <div className="text-sm text-gray-500 font-medium">
                Showing <span className="text-brand-black font-bold">{totalElements}</span> vehicles
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-none mt-1">Sort by</span>
                <div className="relative group">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 pl-4 pr-10 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#111111] cursor-pointer"
                  >
                    <option>Featured</option>
                    <option>Price: Low to High</option>
                    <option>Price: High to Low</option>
                    <option>Rating</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-10 h-10 border-4 border-brand-black border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : cars.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mb-10">
                  {cars.map((car) => (
                    <CarCard
                      key={car.id}
                      car={car}
                      isWishlisted={wishlistIds.includes(car.id)}
                      onToggleWishlist={isRenter ? handleToggleWishlist : undefined}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <button
                      disabled={page === 0}
                      onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-bold text-brand-black hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm font-bold text-gray-500">
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                      className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-bold text-brand-black hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <Search className="text-gray-400 w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No vehicles found</h3>
                <p className="text-gray-500 text-sm max-w-xs mx-auto mb-8">
                  We couldn't find any vehicles matching your current filters. Try adjusting your search, city, or category.
                </p>
                <button
                  onClick={clearFilters}
                  className="bg-brand-black text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-gray-800 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {isSidebarOpen && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm lg:hidden transition-opacity">
          <div className="absolute right-0 top-0 bottom-0 flex w-80 max-w-[88vw] flex-col bg-white shadow-2xl animate-in fade-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-6">
              <h2 className="text-2xl font-black text-brand-black">Filters</h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6">
              <div className="space-y-10 pb-6">
              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">By Name</h3>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search model..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#111111] outline-none"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">By Category</h3>
                <div className="grid grid-cols-2 gap-3">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-3 rounded-xl border text-[13px] font-bold transition-all ${activeCategory === cat ? 'bg-brand-black border-brand-black text-white shadow-lg' : 'bg-white border-gray-100 text-gray-600 hover:border-gray-300'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">By City</h3>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                >
                  <option value="">All Cities</option>
                  {cities.map((city) => (
                    <option key={city.id || city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">Transmission</h3>
                <select
                  value={selectedTransmission}
                  onChange={(e) => setSelectedTransmission(e.target.value)}
                  className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                >
                  <option value="">All Types</option>
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                </select>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">Fuel Type</h3>
                <select
                  value={selectedFuelType}
                  onChange={(e) => setSelectedFuelType(e.target.value)}
                  className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                >
                  <option value="">All Fuel Types</option>
                  <option value="Petrol">Petrol</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Electric">Electric</option>
                </select>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">Seats</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    min="1"
                    placeholder="Min"
                    value={minSeats}
                    onChange={(e) => setMinSeats(e.target.value)}
                    className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="Max"
                    value={maxSeats}
                    onChange={(e) => setMaxSeats(e.target.value)}
                    className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-5">Price Range</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    min="0"
                    placeholder="Min RF"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Max RF"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#111111]"
                  />
                </div>
              </div>

              <div className="pt-8 border-t border-gray-100">
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="w-full bg-brand-black text-white py-4 rounded-xl font-bold shadow-xl shadow-[#111111]/10"
                >
                  Apply Filters
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BrowseFleet;
